import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Box,
  Grid,
  IconButton,
  Typography,
  InputAdornment,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { proveedoresList, conceptosGasto } from './posData';

const NewExpenseDialog = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    fechaHora: new Date(),
    concepto: '',
    valor: '',
    medioPago: 'efectivo',
    proveedor: null,
    descripcion: '',
    documento: null,
  });
  const [errors, setErrors] = useState({});

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const medioPagoOptions = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta_debito', label: 'Tarjeta Débito' },
    { value: 'tarjeta_credito', label: 'Tarjeta Crédito' },
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'cheque', label: 'Cheque' },
  ];

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Limpiar error del campo
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null,
      });
    }
  };

  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      fechaHora: newDate,
    });
  };

  const handleProveedorChange = (event, newValue) => {
    setFormData({
      ...formData,
      proveedor: newValue,
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        documento: file,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.concepto.trim()) {
      newErrors.concepto = 'El concepto es requerido';
    }

    if (!formData.valor || parseFloat(formData.valor) <= 0) {
      newErrors.valor = 'El valor debe ser mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const gastoData = {
        id: `EXP${Date.now()}`,
        fecha: formData.fechaHora.toISOString().split('T')[0],
        hora: formData.fechaHora.toTimeString().slice(0, 5),
        tipo: 'gasto',
        concepto: formData.concepto,
        valor: parseFloat(formData.valor),
        medioPago: medioPagoOptions.find(mp => mp.value === formData.medioPago)?.label || formData.medioPago,
        cliente: null,
        proveedor: formData.proveedor?.nombre || null,
        empleado: 'Usuario Actual',
        estado: 'completada',
        descripcion: formData.descripcion,
        documento: formData.documento?.name || null,
      };

      onSave(gastoData);
      handleReset();
      onClose();
    }
  };

  const handleReset = () => {
    setFormData({
      fechaHora: new Date(),
      concepto: '',
      valor: '',
      medioPago: 'efectivo',
      proveedor: null,
      descripcion: '',
      documento: null,
    });
    setErrors({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Estilos para full-screen en móvil
  const getPaperStyles = () => {
    if (isMobile) {
      return {
        borderRadius: 0,
        height: '100%',
        maxHeight: '100%',
        width: '100%',
        maxWidth: '100%',
        m: 0,
      };
    }
    return {
      borderRadius: 2,
      maxHeight: '90vh',
    };
  };

  // Botones de acción grandes para touch
  const actionButtonStyles = isMobile ? {
    minHeight: 56,
    fontSize: '1rem',
    py: 1.5,
  } : {};

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: getPaperStyles(),
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
          // Header más alto en móvil para touch
          minHeight: isMobile ? 64 : 'auto',
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Nuevo Gasto
          </Typography>
          <IconButton 
            onClick={handleClose} 
            size={isMobile ? "medium" : "small"}
            sx={{ 
              // Touch target grande
              minWidth: 48, 
              minHeight: 48,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent 
          sx={{ 
            pt: 3,
            // Scroll independiente
            overflow: 'auto',
            // En móvil, ocupar todo el espacio disponible
            ...(isMobile && {
              height: 'calc(100% - 140px)', // Restar header y footer
            }),
          }}
        >
          <Grid container spacing={isMobile ? 2 : 2}>
            {/* Fecha/Hora */}
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Fecha y Hora"
                value={formData.fechaHora}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: isMobile ? 'medium' : 'medium',
                  },
                }}
              />
            </Grid>

            {/* Concepto */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={conceptosGasto}
                value={formData.concepto}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, concepto: newValue || '' });
                  if (errors.concepto) {
                    setErrors({ ...errors, concepto: null });
                  }
                }}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Concepto"
                    required
                    error={!!errors.concepto}
                    helperText={errors.concepto}
                    size={isMobile ? 'medium' : 'medium'}
                  />
                )}
              />
            </Grid>

            {/* Valor */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Valor"
                type="number"
                fullWidth
                required
                size={isMobile ? 'medium' : 'medium'}
                value={formData.valor}
                onChange={handleChange('valor')}
                error={!!errors.valor}
                helperText={errors.valor}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Medio de Pago */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Medio de Pago"
                fullWidth
                size={isMobile ? 'medium' : 'medium'}
                value={formData.medioPago}
                onChange={handleChange('medioPago')}
              >
                {medioPagoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Proveedor */}
            <Grid item xs={12}>
              <Autocomplete
                options={proveedoresList}
                getOptionLabel={(option) => option.nombre || ''}
                value={formData.proveedor}
                onChange={handleProveedorChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Proveedor"
                    size={isMobile ? 'medium' : 'medium'}
                    placeholder="Seleccionar proveedor"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1">{option.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        NIT: {option.nit}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>

            {/* Descripción */}
            <Grid item xs={12}>
              <TextField
                label="Descripción"
                fullWidth
                multiline
                rows={isMobile ? 4 : 3}
                size={isMobile ? 'medium' : 'medium'}
                value={formData.descripcion}
                onChange={handleChange('descripcion')}
                placeholder="Agregar detalles adicionales..."
              />
            </Grid>

            {/* Adjuntar documento */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: formData.documento ? 'action.hover' : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon color="action" />
                  <Typography variant="body2">
                    {formData.documento ? formData.documento.name : 'Adjuntar documento'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size={isMobile ? "medium" : "small"}
                  component="label"
                  sx={isMobile ? { minHeight: 48 } : {}}
                >
                  Seleccionar
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions 
          sx={{ 
            px: 3, 
            py: 2, 
            borderTop: '1px solid #e0e0e0',
            // Footer fijo en móvil
            ...(isMobile && {
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
            }),
          }}
        >
          <Button 
            onClick={handleClose} 
            color="inherit" 
            variant="outlined"
            sx={actionButtonStyles}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            sx={actionButtonStyles}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default NewExpenseDialog;
